import {
  Dialog,
  DialogTitle,
  Typography,
  IconButton,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Divider,
  TextField,
  FormControlLabel,
  Switch,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Loading from "../../../../components/loading/loading";
import { updateIssueRequest } from "../../../../utils/APICalls";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { useSelector } from "react-redux";
import { userType } from "../../../../utils/Constant";

export default function UpdateIssuesDialog({
  issueIds,
  showDialog,
  closeDialog,
  updateSuccess,
}: any) {
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { currentUser } = useSelector((state: any) => state.user.user);

  const validationSchema = Yup.object({
    description: Yup.string().required("This field can't be empty"),
    isResolved: Yup.boolean(),
  });

  const formik = useFormik({
    initialValues: {
      description: "",
      isResolved: false,
    },
    validationSchema,
    validateOnChange: true,
    onSubmit: (values) => {
      const payload: any = {
        description: values.description,
        isResolved: values.isResolved,
        issueIds,
      };
      updateIssueStatus(payload);
    },
  });

  const updateIssueStatus = async (payload: any) => {
    setShowLoading(true);
    const response = await updateIssueRequest(payload);
    setShowLoading(false);
    if (response?.status === 204) {
      updateSuccess();
      closeDialog();
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <Dialog
          open={showDialog}
          onClose={closeDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          fullWidth={true}
          maxWidth={"lg"}
        >
          <DialogTitle id="responsive-dialog-title">
            <Typography variant="h5" color="black">
              Update Selected Issues
            </Typography>
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={closeDialog}
            sx={(theme) => ({
              position: "absolute",
              right: 8,
              top: 8,
              color: "black",
            })}
          >
            <CloseIcon />
          </IconButton>

          <DialogContent dividers>
            <DialogContentText id="alert-dialog-description">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#FFF4E5",
                  px: 2,
                  py: 1,
                }}
              >
                <WarningAmberOutlinedIcon color="primary" />
                <Typography sx={{ fontSize: 14, color: "#663C00", ml: 1 }}>
                  You have multiple orders currently selected. If this wasn’t
                  intentional, close this dialog and select only the orders for
                  which you want to update.
                </Typography>
              </Box>
              <Typography variant="h5" color="black" mt={2}>
                Updates
              </Typography>
              <Box
                sx={{
                  border: "1px solid #E0E0E0",
                  px: 1,
                  mt: 2,
                  borderRadius: 1,
                }}
              >
                <Box className="user-name-layout">
                  <Typography className="label-txt">Update</Typography>
                  <TextField
                    name="description"
                    fullWidth
                    id="outlined-basic"
                    label="Describe the Issue"
                    variant="outlined"
                    multiline
                    onChange={formik.handleChange}
                    value={formik.values.description}
                    rows={2}
                    error={
                      (formik.touched.description || formik.submitCount > 0) &&
                      Boolean(formik.errors.description)
                    }
                    helperText={
                      (formik.touched.description || formik.submitCount > 0) &&
                      formik.errors.description
                    }
                  />
                </Box>
                {currentUser?.userType === userType.tenant && (
                  <>
                    <Divider />
                    <Box className="user-name-layout">
                      <Typography className="label-txt">
                        Issue Resolved?
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              name="isResolved"
                              checked={formik.values.isResolved}
                              onChange={formik.handleChange}
                            />
                          }
                          label={formik.values.isResolved ? "Yes" : "No"}
                        />
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Box>
              <Button variant="outlined" onClick={closeDialog}>
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{ ml: 2 }}
                onClick={() => formik.handleSubmit()}
              >
                save
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      </form>
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          onClose={() => setShowErrorNotification(false)}
          title={errorMessage}
        />
      )}
      {showLoading && <Loading />}
    </>
  );
}
